import { useState, useRef, useEffect } from 'react';
import { cn, getInitials } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { useThemeStore } from '@/stores/theme';
import { useNotificationStore } from '@/stores/notifications';
import { useTimerStore } from '@/stores/timer';
import { useStrategyStore } from '@/stores/strategy';
import { useResearchQueueStore } from '@/stores/researchQueue';
import { SearchDialog } from './SearchDialog';
import { BugReportDialog } from './BugReportDialog';
import { api } from '@/lib/api';
import { navIconButtonClass } from './navIconButton';
import { GuidanceHubMenu } from '@/components/onboarding/GuidanceHubMenu';
import { PageHelpButton } from '@/components/help/PageHelpButton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Search, Bell, Sun, Moon, Monitor, LogOut,
  User, Settings, ChevronDown, Timer, Square,
  BookOpen, Target, Layers, Loader2, Trophy, PenSquare, Bug,
} from 'lucide-react';
import { useGamificationStore } from '@/stores/gamification';
import { DialogTrigger } from '@/components/ui/dialog';
import { Menu } from 'lucide-react';

interface TopNavProps {
  onNavigate: (path: string) => void;
  currentPath: string;
}

function formatQueueElapsed(iso: string) {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.max(1, Math.round(ms / 1000))} s`;
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m} Min.`;
  const h = Math.floor(m / 60);
  return `${h} h ${m % 60} Min.`;
}

export function TopNav({ onNavigate, currentPath }: TopNavProps) {
  const { user, logout } = useAuthStore();
  const roles = (user as { roles?: string[] } | null)?.roles ?? [];
  const isAdmin = roles.includes('admin') || roles.includes('super_admin');
  const { theme, setTheme } = useThemeStore();
  const { unreadCount, fetchUnreadCount } = useNotificationStore();
  const { activeActivityId, activeActivityTitle, elapsed, stopTimer } = useTimerStore();
  const { strategy, togglePanel } = useStrategyStore();
  const researchQueueItems = useResearchQueueStore((s) => s.items);
  const researchQueueLoading = useResearchQueueStore((s) => s.loading);
  const gamification = useGamificationStore((s) => s.profile);
  const gamificationLoading = useGamificationStore((s) => s.loading);
  const fetchResearchQueue = useResearchQueueStore((s) => s.fetchQueue);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [researchQueueOpen, setResearchQueueOpen] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [bugReportsEnabled, setBugReportsEnabled] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const themeRef = useRef<HTMLDivElement>(null);
  const researchQueueRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    const load = () => {
      if (!user) { setBugReportsEnabled(false); return; }
      void api.get<{ data?: { enabled: boolean } }>('/bug-reports/meta?categories=false').then((res) => {
        if (active) setBugReportsEnabled(res.data?.enabled === true);
      }).catch(() => { if (active) setBugReportsEnabled(false); });
    };
    load();
    window.addEventListener('bug-report-settings-changed', load);
    return () => { active = false; window.removeEventListener('bug-report-settings-changed', load); };
  }, [user]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (!user) return;
    void fetchResearchQueue();
    const interval = setInterval(() => void fetchResearchQueue(), 5000);
    return () => clearInterval(interval);
  }, [user, fetchResearchQueue]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setShowProfileMenu(false);
      if (themeRef.current && !themeRef.current.contains(e.target as Node)) setShowThemeMenu(false);
      if (researchQueueRef.current && !researchQueueRef.current.contains(e.target as Node)) setResearchQueueOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        void togglePanel();
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'r') {
        e.preventDefault();
        window.location.reload();
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setShowProfileMenu(false);
        setShowThemeMenu(false);
        setResearchQueueOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [togglePanel]);

  const formatElapsed = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  const themeIcon = theme === 'dark' ? <Moon size={18} /> : theme === 'light' ? <Sun size={18} /> : <Monitor size={18} />;

  return (
    <header
      className="fixed left-0 right-0 top-0 z-30 flex h-16 min-w-0 items-center border-b border-border bg-card md:left-[var(--sidebar-current-width,260px)]"
    >
      <div className="flex min-w-0 w-full items-center justify-between gap-2 px-3 sm:px-6">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <DialogTrigger asChild>
            <button type="button" aria-label="Navigation öffnen" className={cn(navIconButtonClass, 'md:hidden')}><Menu size={20} /></button>
          </DialogTrigger>
          <button
            type="button"
            onClick={() => onNavigate('/composer')}
            aria-label="Neuer Post"
            className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <PenSquare size={16} />
            <span className="hidden sm:inline">Neuer Post</span>
          </button>
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Suchen"
            className="flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-md border border-border bg-background text-sm text-muted transition-colors hover:bg-card-hover xl:w-56 xl:justify-start xl:px-3"
          >
            <Search size={16} />
            <span className="hidden flex-1 text-left xl:inline">Suchen...</span>
            <kbd className="hidden rounded border border-border px-1.5 py-0.5 text-[10px] font-medium xl:inline">Ctrl+K</kbd>
          </button>

          <div className="hidden xl:block"><PageHelpButton currentPath={currentPath} onNavigate={onNavigate} /></div>

          {activeActivityId && (
            <div className="hidden items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 xl:flex">
              <Timer size={14} className="text-muted" />
              <span className="max-w-24 truncate text-xs font-medium text-foreground">{activeActivityTitle}</span>
              <span className="font-mono text-xs font-bold tabular-nums text-muted dark:text-foreground">{formatElapsed(elapsed)}</span>
              <button type="button" onClick={stopTimer} className="rounded p-0.5 text-muted hover:bg-card-hover">
                <Square size={12} />
              </button>
            </div>
          )}
        </div>
        <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} onNavigate={onNavigate} />

        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          {/* Marketing Research queue (background runs) */}
          <div ref={researchQueueRef} className="relative hidden lg:block">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => {
                    setResearchQueueOpen((o) => !o);
                    void fetchResearchQueue();
                  }}
                  aria-label="Research-Warteschlange öffnen"
                  className={cn(
                    navIconButtonClass,
                    'relative',
                    researchQueueOpen && 'bg-card-hover text-foreground'
                  )}
                >
                  <span className="relative inline-flex shrink-0">
                    <Layers size={18} className={cn(researchQueueLoading && 'opacity-60')} />
                    {researchQueueLoading && (
                      <Loader2
                        size={10}
                        className="absolute -bottom-0.5 -right-0.5 animate-spin text-muted"
                        aria-hidden
                      />
                    )}
                    {researchQueueItems.length > 0 && (
                      <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                        {researchQueueItems.length > 99 ? '99+' : researchQueueItems.length}
                      </span>
                    )}
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                Research-Warteschlange (laufende Marktforschung)
              </TooltipContent>
            </Tooltip>
            {researchQueueOpen && (
              <div className="absolute right-0 top-full z-50 mt-1 max-h-[min(70vh,24rem)] w-[min(100vw-2rem,22rem)] overflow-y-auto rounded-md border border-border bg-card py-2 shadow-md">
                <div className="border-b border-border px-3 pb-2">
                  <p className="text-xs font-semibold text-muted">
                    Research-Warteschlange
                  </p>
                  <p className="text-xs text-muted">
                    Läuft im Hintergrund.
                  </p>
                </div>
                {researchQueueItems.length === 0 ? (
                  <p className="px-3 py-4 text-sm text-muted">
                    Keine laufenden Research-Jobs.
                  </p>
                ) : (
                  <ul className="space-y-1 px-1">
                    {researchQueueItems.map((item) => (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => {
                            onNavigate(`/marketing-strategy?strategy=${encodeURIComponent(item.strategy_id)}`);
                            setResearchQueueOpen(false);
                          }}
                          className="flex w-full flex-col gap-1 rounded-md px-2 py-2 text-left text-sm hover:bg-background dark:hover:bg-card-hover/80"
                        >
                          <span className="font-medium text-foreground">
                            {item.strategy_title}{' '}
                            <span className="font-normal text-muted">({item.year})</span>
                          </span>
                          <span className="line-clamp-2 text-xs text-muted">
                            {item.query}
                          </span>
                          {item.plan?.message && (
                            <span className="line-clamp-3 text-xs text-muted">
                              {item.plan.message}
                            </span>
                          )}
                          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted">
                            <span className="flex items-center gap-1.5">
                              <Loader2 size={12} className="animate-spin shrink-0" />
                              {item.status === 'queued' ? 'Warteschlange…' : 'Research läuft…'}
                            </span>
                            {item.started_at && (
                              <span className="font-mono text-muted">
                                {formatQueueElapsed(item.started_at)}
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => onNavigate('/dashboard')}
                aria-label="Fortschritt und Level"
                className={cn(navIconButtonClass, 'relative hidden xl:flex', gamificationLoading && 'opacity-70')}
              >
                <Trophy size={18} className="shrink-0" aria-hidden />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {gamification != null
                ? `Fortschritt & Level (Dashboard) — L${gamification.level}`
                : 'Fortschritt & Level (Dashboard)'}
            </TooltipContent>
          </Tooltip>

          {/* Strategy Quick-Access Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => void togglePanel()}
                aria-label="Marketingstrategie"
                className={cn(navIconButtonClass, 'relative hidden xl:flex')}
              >
                <Target size={18} />
                {strategy && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-0.5 text-[9px] font-bold text-primary-foreground">
                    {String(strategy.year).slice(2)}
                  </span>
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {`Marketingstrategie${strategy ? ` ${strategy.year}` : ''} (Ctrl+Shift+S)`}
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => onNavigate('/onboarding')}
                aria-label="Onboarding"
                className={cn(navIconButtonClass, 'hidden xl:flex')}
              >
                <BookOpen size={18} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Onboarding</TooltipContent>
          </Tooltip>
          <div ref={themeRef} className="relative">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setShowThemeMenu(!showThemeMenu)}
                  aria-label="Farbschema wechseln"
                  className={navIconButtonClass}
                >
                  {themeIcon}
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Farbschema wechseln</TooltipContent>
            </Tooltip>
            {showThemeMenu && (
              <div className="absolute right-0 top-full mt-1 w-36 rounded-lg border border-border bg-card py-1 shadow-lg">
                {[
                  { value: 'light' as const, label: 'Hell', icon: <Sun size={14} /> },
                  { value: 'dark' as const, label: 'Dunkel', icon: <Moon size={14} /> },
                  { value: 'system' as const, label: 'System', icon: <Monitor size={14} /> },
                ].map(({ value, label, icon }) => (
                  <button
                    key={value}
                    onClick={() => { setTheme(value); setShowThemeMenu(false); }}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-sm',
                      theme === value
                        ? 'bg-card-hover text-foreground'
                        : 'text-muted hover:bg-background dark:hover:bg-card-hover'
                    )}
                  >
                    {icon} {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="hidden lg:block"><GuidanceHubMenu onNavigate={onNavigate} /></div>

          {bugReportsEnabled && <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setBugReportOpen(true)}
                aria-label="Bug melden"
                className={navIconButtonClass}
              >
                <Bug size={18} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Bug melden</TooltipContent>
          </Tooltip>}
          {bugReportsEnabled && <BugReportDialog open={bugReportOpen} onOpenChange={setBugReportOpen} />}

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => onNavigate('/notifications')}
                aria-label="Benachrichtigungen öffnen"
                className={cn(navIconButtonClass, 'relative')}
              >
                <Bell size={18} />
                {unreadCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-danger-foreground">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Benachrichtigungen</TooltipContent>
          </Tooltip>

          <div ref={profileRef} className="relative">
            <button
              aria-label="Benutzermenü"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-card-hover"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-card-hover text-sm font-semibold text-foreground">
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                ) : (
                  getInitials(user?.full_name || 'U')
                )}
              </div>
              {user && (
                <div className="hidden max-w-40 truncate text-left 2xl:block">
                  <div className="text-sm font-medium text-foreground">{user.full_name}</div>
                </div>
              )}
              <ChevronDown size={14} className="text-muted" />
            </button>
            {showProfileMenu && (
              <div className="absolute right-0 top-full mt-1 w-56 rounded-lg border border-border bg-card py-1 shadow-lg">
                <div className="border-b border-border px-3 py-2">
                  <p className="text-sm font-medium text-foreground">{user?.full_name}</p>
                  <p className="text-xs text-muted">{user?.email}</p>
                </div>
                <button
                  onClick={() => { onNavigate('/profile'); setShowProfileMenu(false); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-muted hover:bg-background dark:hover:bg-card-hover"
                >
                  <User size={14} /> Profil
                </button>
                {isAdmin && <button
                  onClick={() => { onNavigate('/admin/settings'); setShowProfileMenu(false); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-muted hover:bg-background dark:hover:bg-card-hover"
                >
                  <Settings size={14} /> Einstellungen
                </button>}
                <div className="border-t border-border" />
                <button
                  onClick={() => { logout(); setShowProfileMenu(false); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-danger hover:bg-danger-soft"
                >
                  <LogOut size={14} /> Abmelden
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
